import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../../store';
import { fetchWellNoteEvents } from '../../services/dashboard/WellNoteEventsService';

// Event data type
export interface EventData {
  id: number | string;
  wellId: string | number | null;
  wellGroupId?: number | null;
  categoryId: number;
  description: string;
  timestamp: string; // ISO string
  type: string; // 'Note', 'Downtime', etc.
  Source?: string;
  // New API fields (optional for backward compatibility)
  Category?: string;
  Comment?: string;
  Username?: string;
  Created?: string;
}

// State interface
interface EventsState {
  events: EventData[];
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
  currentRequestId?: string;
}

// Initial state
const initialState: EventsState = {
  events: [],
  status: 'idle',
  error: null,
  currentRequestId: undefined,
};

// Async thunk
export const fetchPumpCheckerEvents = createAsyncThunk<
  EventData[],
  { wellId: number | undefined; assetId: string | undefined; startDate?: string; endDate?: string; isESP: boolean },
  { rejectValue: string }
>('events/fetchPumpCheckerEvents', async ({ wellId, assetId, startDate, endDate, isESP }) => {
  try {
    const response = await fetchWellNoteEvents(wellId, assetId, startDate, endDate, isESP);
    return response;
  } catch (err: unknown) {
    return [] as EventData[]; // Return empty array on error
  }
});

const wellNoteSlice = createSlice({
  name: 'events',
  initialState,
  reducers: {
    clearEvents(state) {
      state.events = [];
      state.status = 'idle';
      state.error = null;
      state.currentRequestId = undefined;
    },
    addWellNote(state, action: PayloadAction<EventData>) {
      state.events.push(action.payload);
    },
    setWellNote(state, action: PayloadAction<EventData[]>) {
      state.events = action.payload;
      state.status = 'succeeded';
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPumpCheckerEvents.pending, (state, action) => {
        state.status = 'loading';
        state.error = null;
        state.currentRequestId = action.meta.requestId;
      })
      .addCase(fetchPumpCheckerEvents.fulfilled, (state, action) => {
        // Ignore stale responses from earlier requests
        if (state.currentRequestId !== action.meta.requestId) return;
        state.status = 'succeeded';
        state.events = action.payload;
        state.currentRequestId = undefined;
      })
      .addCase(fetchPumpCheckerEvents.rejected, (state, action) => {
        if (state.currentRequestId !== action.meta.requestId) return;
        state.status = 'failed';
        state.error = action.payload ?? 'Error fetching events';
        state.currentRequestId = undefined;
      });
  },
});

// Actions
export const { clearEvents, addWellNote, setWellNote } = wellNoteSlice.actions;

// Selectors
export const selectAllEvents = (state: RootState) => state.WellNoteSlice.events;

export const selectEventsByWellId = (wellId: number) => (state: RootState) =>
  state.WellNoteSlice.events.filter((e) => e.wellId === wellId);

export const selectEventsByDateRange = (start: string, end: string) => (state: RootState) =>
  state.WellNoteSlice.events.filter((e) => {
    const eventDate = e['Created'] || e.timestamp;
    return eventDate && eventDate >= start && eventDate <= end;
  });

export const selectEventsStatus = (state: RootState) => state.WellNoteSlice.status;
export const selectEventsError = (state: RootState) => state.WellNoteSlice.error;

// Reducer
export default wellNoteSlice.reducer;
